/**
 * ENG-5116 — track consecutive ticks where an agent's persistent session
 * is unhealthy AND we didn't attempt a respawn this tick.
 *
 * Vigil's incident: day-rollover stopped the tmux session at 00:02, then
 * for >6 hours the manager polled every ~30s but the spawn branch never
 * fired and never logged why. The bulk-heartbeat update at the API kept
 * `last_heartbeat_at` fresh, so neither the customer nor the cloud
 * heartbeat alarm caught it. This tracker is the local-manager half of
 * the answer: once a session has been unhealthy without a respawn
 * attempt for `threshold` consecutive ticks, log a loud WARN so the
 * operator (or future Ops agent / synthetic prober) can correlate.
 *
 * ENG-9614 — it now tracks a SECOND, opposite streak: unhealthy WITH a spawn
 * attempted, every tick. The scratch/tmp collision produced exactly that shape
 * (claude died on ENOTDIR the instant it started, so the manager respawned
 * forever) and the original counter could not see it, because attempting a
 * spawn is one of its two reset conditions. Two questions, two counters - see
 * `spawnLoopCounts`.
 *
 * Pure state held in a Map — no I/O, no timers — so it's safe to unit
 * test and the manager's per-tick code drives it.
 */

export interface StuckTickInput {
  codeName: string;
  sessionHealthy: boolean;
  spawnAttempted: boolean;
}

export interface StuckTickResult {
  /** Number of consecutive ticks the agent has been unhealthy with no spawn. */
  consecutiveTicks: number;
  /** True on the tick we cross the warning threshold (caller logs). */
  shouldWarn: boolean;
  /** True when the counter just reset (caller can log recovery if useful). */
  recovered: boolean;
  /**
   * ENG-9614: consecutive ticks where a spawn WAS attempted and the session was
   * STILL unhealthy afterwards - i.e. the respawn is not sticking.
   */
  spawnLoopTicks: number;
  /** True on the tick the spawn-loop streak crosses its threshold. */
  shouldWarnSpawnLoop: boolean;
}

/**
 * Default WARN threshold: 3 consecutive ticks. At a ~30s poll interval
 * that's ~90s of an agent being silently down, which beats the 6h Vigil
 * outage by several orders of magnitude without being so noisy that
 * single transient blips trigger pages.
 */
export const DEFAULT_STUCK_WARN_THRESHOLD = 3;

/**
 * ENG-9614 — WARN threshold for the OTHER shape: a respawn that keeps being
 * attempted and keeps not sticking.
 *
 * Same value as the no-spawn threshold and for the same reason: at a ~21-30s
 * poll that is roughly a minute of an agent being down, which is well inside
 * the 3m48s the live scratch/tmp incident ran for, and far enough above a
 * single transient blip not to page on one.
 */
export const DEFAULT_SPAWN_LOOP_WARN_THRESHOLD = 3;

export class PersistentSessionStuckTracker {
  private readonly counts = new Map<string, number>();
  private readonly warned = new Set<string>();
  private readonly threshold: number;
  /**
   * ENG-9614 — the second streak, held separately because it answers a
   * DIFFERENT question and must not inherit the first one's reset rule.
   *
   * `counts` above resets whenever `spawnAttempted` is true, which is correct
   * for its own question (ENG-5116/Vigil: unhealthy and nobody even TRIED).
   * But it makes the tracker structurally blind to the inverse: the scratch/tmp
   * collision attempted a spawn on EVERY tick, so `counts` was deleted on every
   * tick, `shouldWarn` could never fire, and an agent looped unrecoverably for
   * 3m48s without this class emitting a single line.
   *
   * Folding both into one counter was considered and rejected - a shared
   * counter would have to pick one reset rule, and either choice makes the
   * other question unanswerable. Two questions, two counters.
   */
  private readonly spawnLoopCounts = new Map<string, number>();
  private readonly spawnLoopWarned = new Set<string>();
  private readonly spawnLoopThreshold: number;

  constructor(
    threshold: number = DEFAULT_STUCK_WARN_THRESHOLD,
    spawnLoopThreshold: number = DEFAULT_SPAWN_LOOP_WARN_THRESHOLD,
  ) {
    if (threshold < 1) throw new Error('stuck-tracker threshold must be >= 1');
    if (spawnLoopThreshold < 1) throw new Error('stuck-tracker spawn-loop threshold must be >= 1');
    this.threshold = threshold;
    this.spawnLoopThreshold = spawnLoopThreshold;
  }

  /**
   * Advance the spawn-loop streak for one tick.
   *
   * Counts only when a spawn was attempted AND the session is still unhealthy.
   * Health alone resets it - an agent that came up is not looping, however it
   * got there. A tick with no spawn attempt neither counts nor resets: that is
   * the OTHER failure, already owned by `counts`, and letting it clear this
   * streak would let the two shapes alternate and hide each other.
   */
  private recordSpawnLoop(
    codeName: string,
    sessionHealthy: boolean,
    spawnAttempted: boolean,
  ): { ticks: number; shouldWarn: boolean } {
    if (sessionHealthy) {
      this.spawnLoopCounts.delete(codeName);
      this.spawnLoopWarned.delete(codeName);
      return { ticks: 0, shouldWarn: false };
    }
    if (!spawnAttempted) {
      return { ticks: this.spawnLoopCounts.get(codeName) ?? 0, shouldWarn: false };
    }
    const next = (this.spawnLoopCounts.get(codeName) ?? 0) + 1;
    this.spawnLoopCounts.set(codeName, next);
    const crossed = next >= this.spawnLoopThreshold && !this.spawnLoopWarned.has(codeName);
    if (crossed) this.spawnLoopWarned.add(codeName);
    return { ticks: next, shouldWarn: crossed };
  }

  /**
   * Record one tick for one agent. Returns whether the caller should log
   * a stuck-warning this tick. Re-warning only fires when the counter
   * climbs above the threshold *again* after a recovery, so a stuck
   * agent doesn't spam the log every 30s — operators get one WARN per
   * stuck streak.
   */
  record(input: StuckTickInput): StuckTickResult {
    const { codeName, sessionHealthy, spawnAttempted } = input;
    const spawnLoop = this.recordSpawnLoop(codeName, sessionHealthy, spawnAttempted);
    const wasStuck = (this.counts.get(codeName) ?? 0) >= this.threshold;

    // Recovery condition: either the session is healthy, OR we made a
    // genuine respawn attempt this tick. Both reset the streak; the
    // attempt path matters because the next tick will observe whether
    // it actually came up.
    if (sessionHealthy || spawnAttempted) {
      this.counts.delete(codeName);
      const wasWarned = this.warned.delete(codeName);
      return {
        consecutiveTicks: 0,
        shouldWarn: false,
        recovered: wasStuck && wasWarned,
        spawnLoopTicks: spawnLoop.ticks,
        shouldWarnSpawnLoop: spawnLoop.shouldWarn,
      };
    }

    const next = (this.counts.get(codeName) ?? 0) + 1;
    this.counts.set(codeName, next);

    // Only warn on the first tick that crosses the threshold — set
    // membership in `warned` debounces re-fires until a recovery.
    const crossed = next >= this.threshold && !this.warned.has(codeName);
    if (crossed) this.warned.add(codeName);

    return {
      consecutiveTicks: next,
      shouldWarn: crossed,
      recovered: false,
      spawnLoopTicks: spawnLoop.ticks,
      shouldWarnSpawnLoop: spawnLoop.shouldWarn,
    };
  }

  /** Test/debug helper: peek at an agent's current streak. */
  peek(codeName: string): number {
    return this.counts.get(codeName) ?? 0;
  }

  /** Test/debug helper: peek at an agent's current spawn-loop streak. */
  peekSpawnLoop(codeName: string): number {
    return this.spawnLoopCounts.get(codeName) ?? 0;
  }

  /** Test helper: clear all state. */
  reset(): void {
    this.counts.clear();
    this.warned.clear();
    this.spawnLoopCounts.clear();
    this.spawnLoopWarned.clear();
  }
}
